/* eslint-disable complexity */
import React, {Component} from 'react';
import ReactDOM from 'react-dom';
import {UitkEGMaps, UitkEGMapsQualifier, UitkEGMapsType} from 'uitk-react-map';
import {SVGIcon} from '@homeaway/react-svg';
import {FOURSQUARE_GREY} from '../../svgDefs/FOURSQUARE_GREY';
import {cfg} from '../../utils/EGMapsMarkerConfig';
import {EGMapsMapHit} from '../EGMapsMapHit/EGMapsMapHit';
import {APP_NAMES} from '../../constants';
import MapContext from '../../context/MapContext';
import OverlayContent from '../OverlayContent/OverlayContent';
import {BrandProvider} from '@shared-ui/brand-context';

import '@eg-maps/base/public/egmaps.css';

export default class GoogleMapWrapper extends Component {
    constructor(props) {
        super(props);
        this.mapZoom = this.props.zoom;
        this.mapRef = React.createRef();
    }

    static contextType = MapContext;

    beforeInit = (map) => {
        map.addIdleListener(this.handleIdle);
        map.addMarkerChangeListener(this.onMarkerChange);
        map.addSelectionChangeListener(this.handleMarkerSelect);
        map.addHoverChangeListener(this.handleOnHoverChange);
        map.addBoundChangeListener(this.handleOnBoundsChanged);
    };

    handleMapLoaded = (map) => {
        if (typeof this.props.onMapLoaded === 'function') {
            this.props.onMapLoaded(map);
        }
    };

    onMapClick = () => {
        if (typeof this.props.onMapClick === 'function') {
            this.props.onMapClick();
        }
    };

    handleZoomIn = (center, bounds, zoom) => {
        if (typeof this.props.onZoomIn === 'function') {
            this.props.onZoomIn();
        }
        if (typeof this.props.onZoomChanged === 'function') {
            this.props.onZoomChanged(bounds, zoom);
        }
        if (typeof this.props.onMapUpdated === 'function') {
            this.props.onMapUpdated(bounds, zoom);
        }
    };

    handleZoomOut = (center, bounds, zoom) => {
        if (typeof this.props.onZoomOut === 'function') {
            this.props.onZoomOut();
        }
        if (typeof this.props.onZoomChanged === 'function') {
            this.props.onZoomChanged(bounds, zoom);
        }
        if (typeof this.props.onMapUpdated === 'function') {
            this.props.onMapUpdated(bounds, zoom);
        }
    };

    handleMapMove = (center, bounds, zoomLevel) => {
        if (this.props.shouldCallMapPan) {
            if (typeof this.props.onMapUpdated === 'function') {
                this.props.onMapUpdated(bounds, zoomLevel);
            }
            if (typeof this.props.onDragEnd === 'function') {
                this.props.onDragEnd(bounds, zoomLevel);
            }
        } else {
            this.props.setShouldCallMapPan(true);
        }
    };

    handleOnBoundsChanged = ({originalEvent, bounds, zoom}) => {
        if (typeof this.props.onBoundsChanged === 'function') {
            this.props.onBoundsChanged();
        }
        if (
            originalEvent === 'zoomOut' ||
            originalEvent === 'zoomOutKeyboard'
        ) {
            this.handleZoomOut(bounds.center, bounds, zoom);
        } else if (
            originalEvent === 'zoomIn' ||
            originalEvent === 'zoomInKeyboard'
        ) {
            this.handleZoomIn(bounds.center, bounds, zoom);
        } else if (
            originalEvent === 'drag' ||
            originalEvent === 'moveKeyboard' ||
            originalEvent === 'touch'
        ) {
            this.handleMapMove(bounds.center, bounds, zoom);
        }
    };

    handleOnHoverChange = ({feature, setPopupContent, action}) => {
        if (
            this.context.appName === APP_NAMES.LANDINGAPP &&
            feature.type === 'place' &&
            action === 'mouseEnter'
        ) {
            this.generateLandingAppPOIOverlay(feature.id, setPopupContent);
        }
        if (
            this.context.appName === APP_NAMES.LANDINGAPP &&
            feature.type === 'place' &&
            action === 'mouseLeave'
        ) {
            this.generateLandingAppPOIOverlay(null, setPopupContent);
        }
        if (typeof this.props.onHoverChange === 'function') {
            this.props.onHoverChange(feature, setPopupContent, action);
        }
    };

    onMarkerChange = () => {
        if (typeof this.props.onMarkerChange === 'function') {
            this.props.onMarkerChange();
        }
    };

    handleIdle = () => {
        if (typeof this.props.onIdle === 'function') {
            this.props.onIdle();
        }
    };

    generateSerpOverlay = (feature, setPopupContent) => {
        const {MapHitComponentProvider} = this.props;
        const infoWindowOptions = this.props.overlayMapOptions.get(feature.id);
        const {
            pixelOffset,
            infoBoxClearance,
            detailPageUrl,
            bathrooms,
            bedrooms,
            sleeps,
            images,
            priceSummary,
            reviewCount,
            superlativesBadges,
            averageRating,
            propertyType,
            tripBoardButton,
        } = infoWindowOptions || {};
        if (infoWindowOptions) {
            const popupOffset = {
                transform: `transformX(${pixelOffset.width}), transformY(${pixelOffset.height})`,
                padding: `${infoBoxClearance.width}, ${infoBoxClearance.height}`,
            };
            const popupContent = (
                <EGMapsMapHit
                    style={popupOffset}
                    infoWindowOptions={{
                        detailPageUrl,
                        pixelOffset,
                        infoBoxClearance,
                        bathrooms,
                        bedrooms,
                        sleeps,
                        images,
                        priceSummary,
                        reviewCount,
                        superlativesBadges,
                        averageRating,
                        propertyType,
                        tripBoardButton,
                    }}
                    hitIndex={1}
                    MapHitComponentProvider={MapHitComponentProvider}
                />
            );
            const wrappingElement = document.createElement('div');
            ReactDOM.render(popupContent, wrappingElement);
            setPopupContent(wrappingElement);
        } else {
            setPopupContent();
        }
    };
    // TODO: see if removing this function breaks anything (the function call on line 203 doesn't cause problems when commented out)
    generatePDPOverlay = (marker, setPopupContent) => {
        const infoWindowOptions = this.props.overlayMapOptions.get(marker.id);
        const {name, distance} = infoWindowOptions || {};
        if (name && distance) {
            const popupContent = (
                <OverlayContent
                    name={name}
                    distance={distance}
                    labels={this.context.labels}
                />
            );
            const wrappingElement = document.createElement('div');
            ReactDOM.render(popupContent, wrappingElement);
            setPopupContent(wrappingElement);
        } else {
            setPopupContent();
        }
    };

    generateLandingAppPOIOverlay = (markerId, setPopupContent) => {
        if (markerId) {
            const infoWindowOptions =
                this.props.overlayMapOptions.get(markerId);
            const {content} = infoWindowOptions || {};
            if (content) {
                const popupcontent = (
                    <div className={content.props.className}>
                        <a
                            className={content.props.children.props.className}
                            href={content.props.children.props.href}
                        >
                            {content.props.children.props.children}
                        </a>
                    </div>
                );
                const wrappingElement = document.createElement('div');
                ReactDOM.render(popupcontent, wrappingElement);
                setPopupContent(wrappingElement);
            } else {
                setPopupContent();
            }
        }
    };

    handleMarkerSelect = ({feature, setPopupContent, action}) => {
        if (action === 'clearSelection') {
            this.handleMarkerDeselect();
        } else if (action === 'select') {
            switch (this.context.appName) {
                case APP_NAMES.SERP:
                    if (typeof this.props.onMarkerClick === 'function') {
                        this.props.onMarkerClick(feature);
                    }
                    this.generateSerpOverlay(feature, setPopupContent);
                    break;
                case APP_NAMES.PDP:
                    if (typeof this.props.onMarkerClick === 'function') {
                        this.props.onMarkerClick(feature);
                    }
                    this.generatePDPOverlay(feature, setPopupContent);
                    break;
                case APP_NAMES.LANDINGAPP:
                    if (typeof this.props.onMarkerClick === 'function') {
                        this.props.onMarkerClick(feature);
                    }
                    this.generateLandingAppPOIOverlay(
                        feature.id,
                        setPopupContent
                    );
                    break;
                case APP_NAMES.TRIPBOARDS:
                    if (typeof this.props.onMarkerClick === 'function') {
                        this.props.onMarkerClick(feature);
                    }
                    break;
                default:
                    break;
            }
        }
    };

    handleMarkerDeselect = () => {
        if (typeof this.props.onMapClick === 'function') {
            this.props.onMapClick();
        }
    };

    setGoogleMapRef = (map) => {
        this.googleMap = map;
    };

    render() {
        const {
            lat,
            lng,
            options,
            source,
            children,
            markers,
            center,
            exact,
            bounds,
        } = this.props;

        const Source = {
            FOURSQUARE: 'foursquare',
            GAIA: 'gaia',
            TRIPOSO: 'triposo',
        };

        let newCenter = center ? center : null;
        if (lat && lng) {
            newCenter = {lat, lng};
        }
        let circle = {};
        // Add ambiguous circle
        if (!exact && lat && lng) {
            circle = {
                lat,
                lng,
                type: 'property',
                status: 'available',
                qualifiers: [UitkEGMapsQualifier.OBFUSCATED],
            };
        }
        const {
            mapTypeControl,
            streetViewControl,
            zoomControl,
            fullscreenControl,
        } = options;
        const markersConfig = cfg(markers, this.context.appName);
        const newMarkers =
            Object.values(circle).length > 0 ? [...markers, circle] : markers;
        const boundsForEGMaps = bounds && {
            ne: [bounds.neLat, bounds.neLong],
            sw: [bounds.swLat, bounds.swLong],
        };
        const initialViewport =
            boundsForEGMaps ||
            newMarkers?.map((m) => ({
                lat: m.lat,
                lng: m.lng,
            }));

        return (
            <>
                <BrandProvider config={{getIcon: () => () => <></>}}>
                    <UitkEGMaps
                        accessToken={this.context.apiKey}
                        height={'100%'}
                        mapType={UitkEGMapsType.GOOGLE}
                        mapConfig={{
                            markers: markersConfig,
                            googleMaps: {
                                padding: 100,
                                gmapsAddConfig: {
                                    center: newCenter,
                                    zoomControl,
                                    mapTypeControl,
                                    streetViewControl,
                                    fullscreenControl,
                                    zoomControlOptions: {
                                        position:
                                            this.context.appName ===
                                                APP_NAMES.SERP && 5,
                                    },
                                },
                            },
                            style: {
                                '.popup': {
                                    padding:
                                        this.context.appName ===
                                        APP_NAMES.LANDINGAPP
                                            ? '6px 6px 4px'
                                            : '0',
                                    boxShadow:
                                        this.context.appName !==
                                            APP_NAMES.LANDINGAPP && 'none',
                                },
                                '.popup.above.google-maps': {
                                    transform: 'translateY(-48px)',
                                },
                            },
                        }}
                        innerRef={this.mapRef}
                        initialViewport={initialViewport}
                        features={newMarkers}
                        beforeInit={this.beforeInit}
                        onInit={this.handleMapLoaded}
                        classNames={['Map']}
                    >
                        {children}
                    </UitkEGMaps>
                </BrandProvider>
                {source === Source.FOURSQUARE && (
                    <SVGIcon
                        markup={FOURSQUARE_GREY}
                        className="foursquare-label"
                        inlineFlex
                        aria-hidden
                    />
                )}
            </>
        );
    }
}
